import React, { useEffect, useState } from "react";
import "./PermissionsTable.css"; // Import the CSS file for styles
import { features } from "../../../constants";
import { capitalize } from "lodash";

type Permission = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  read_by_id: boolean;
};

export const PermissionsTable = (props: any) => {
  const { setSelectedPermissions, selectedPermissions } = props;

  console.log("Selected Permissions: ", selectedPermissions);

  // Initialize permissions state based on features
  const [permissions, setPermissions] = useState<Record<string, Permission>>(
    features.reduce((acc, feature) => {
      acc[feature.name.toLowerCase()] = {
        create: false,
        read: false,
        update: false,
        delete: false,
        read_by_id: false,
      };
      return acc;
    }, {} as Record<string, Permission>)
  );

  const handleSelectAll = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = e.target.checked;
    setPermissions((prev) => ({
      ...prev,
      [features[index].name.toLowerCase()]: {
        create: isChecked,
        read: isChecked,
        update: isChecked,
        delete: isChecked,
        read_by_id: isChecked,
      },
    }));
  };

  const handlePermissionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    featureName: string
  ) => {
    const { name, checked } = e.target;
    setPermissions((prev) => ({
      ...prev,
      [featureName]: {
        ...prev[featureName],
        [name]: checked, // Fix: Update the specific permission based on the name
      },
    }));
  };

  useEffect(() => {
    if (selectedPermissions) {
      setPermissions((prev) => {
        const updatedPermissions = { ...prev };
        Object.keys(updatedPermissions).forEach((key) => {
          updatedPermissions[key] = {
            create: selectedPermissions[key]?.includes("create") || false,
            read: selectedPermissions[key]?.includes("read") || false,
            update: selectedPermissions[key]?.includes("update") || false,
            delete: selectedPermissions[key]?.includes("delete") || false,
            read_by_id:
              selectedPermissions[key]?.includes("read_by_id") || false,
          };
        });
        return updatedPermissions;
      });
    }
  }, [selectedPermissions]);

  useEffect(() => {
    setSelectedPermissions(
      Object.entries(permissions).reduce((acc, [key, value]) => {
        acc[key] = Object.entries(value)
          .filter(([_, hasPermission]) => hasPermission)
          .map(([permission]) => permission);
        return acc;
      }, {}),
      null,
      2
    );
  }, [permissions]);

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Feature</th>
          <th>
            <div className="form-check form-check-primary">
              <input
                className="form-check-input"
                id="customCheckcolor5"
                type="checkbox"
                checked={Object.values(permissions).every(
                  (perm: Permission) =>
                    perm.create && perm.read && perm.update && perm.delete
                )}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setPermissions((prev) => {
                    const updatedPermissions = { ...prev };
                    Object.keys(updatedPermissions).forEach((key) => {
                      updatedPermissions[key] = {
                        create: isChecked,
                        read: isChecked,
                        update: isChecked,
                        delete: isChecked,
                        read_by_id: isChecked,
                      };
                    });
                    return updatedPermissions;
                  });
                }}
              />
            </div>
          </th>
          <th>Permissions</th>
        </tr>
      </thead>
      <tbody>
        {features.map((feature, index) => (
          <tr key={index}>
            <td>{capitalize(feature.name)}</td>
            <td>
              <div className="form-check form-check-primary">
                <input
                  className="form-check-input"
                  id={`customCheckcolorFeature${index}`}
                  type="checkbox"
                  checked={Object.values(
                    permissions[feature.name.toLowerCase()] || {}
                  ).every(Boolean)}
                  onChange={(e) => handleSelectAll(index, e)}
                />
              </div>
            </td>
            <td className="permissions-column">
              <div className="form-check form-check-primary">
                <input
                  className="form-check-input"
                  id={`customCheckcolorCreate${index}`}
                  type="checkbox"
                  checked={
                    permissions[feature.name.toLowerCase()]?.create || false
                  }
                  name="create"
                  onChange={(e) =>
                    handlePermissionChange(e, feature.name.toLowerCase())
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`customCheckcolorCreate${index}`}
                >
                  Create
                </label>
              </div>
              <div className="form-check form-check-primary">
                <input
                  className="form-check-input"
                  id={`customCheckcolorRead${index}`}
                  type="checkbox"
                  checked={
                    permissions[feature.name.toLowerCase()]?.read || false
                  }
                  name="read"
                  onChange={(e) =>
                    handlePermissionChange(e, feature.name.toLowerCase())
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`customCheckcolorRead${index}`}
                >
                  Read all {feature?.name}
                </label>
              </div>
              <div className="form-check form-check-primary">
                <input
                  className="form-check-input"
                  id={`customCheckcolorReadById${index}`}
                  type="checkbox"
                  checked={
                    permissions[feature.name.toLowerCase()]?.read_by_id || false
                  }
                  name="read_by_id"
                  onChange={(e) =>
                    handlePermissionChange(e, feature.name.toLowerCase())
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`customCheckcolorReadById${index}`}
                >
                  Read {feature?.name} by ID
                </label>
              </div>
              <div className="form-check form-check-primary">
                <input
                  className="form-check-input"
                  id={`customCheckcolorUpdate${index}`}
                  type="checkbox"
                  checked={
                    permissions[feature.name.toLowerCase()]?.update || false
                  }
                  name="update"
                  onChange={(e) =>
                    handlePermissionChange(e, feature.name.toLowerCase())
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`customCheckcolorUpdate${index}`}
                >
                  Update
                </label>
              </div>
              <div className="form-check form-check-primary">
                <input
                  className="form-check-input"
                  id={`customCheckcolorDelete${index}`}
                  type="checkbox"
                  checked={
                    permissions[feature.name.toLowerCase()]?.delete || false
                  }
                  name="delete"
                  onChange={(e) =>
                    handlePermissionChange(e, feature.name.toLowerCase())
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`customCheckcolorDelete${index}`}
                >
                  Delete
                </label>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
